/**
 * Chart Abstraction Export Dropdown
 * 
 * A self-contained export dropdown that includes standard export options
 * plus the Chart Abstraction List export. Replaces the existing export
 * button/dropdown in the Quality Summary table header.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, ChevronDown, FileText, FileSpreadsheet, ClipboardList } from "lucide-react";
import { useChartAbstraction } from "@/contexts/ChartAbstractionContext";
import { ChartAbstractionExportModal } from "./ChartAbstractionExportModal";

interface MeasureRow {
  id: string;
  title: string;
  den?: number;
  met?: number;
  not?: number;
  ex?: number;
  exc?: number;
  [key: string]: any;
}

interface ChartAbstractionExportDropdownProps {
  /** Function to export the summary */
  onExportSummary: () => void;
  /** Number of measures in the summary */
  measureCount: number;
  /** Whether there are partial measures to export */
  hasPartialMeasures: boolean;
  /** Count of partial measures */
  partialMeasuresCount: number;
  /** Function to open partial measures modal */
  onExportPartialMeasures: () => void;
  /** Practice ID for the export */
  practiceId: string;
  /** Practice name for the export filename */
  practiceName: string;
  /** All measures data for the Chart Abstraction export */
  measures: MeasureRow[];
  /** Active view lane — the export matches what's on screen (defaults GROUP) */
  evaluationType?: 'GROUP' | 'NPI';
  /** Selected provider when in NPI view */
  selectedNpi?: string;
}

export function ChartAbstractionExportDropdown({
  onExportSummary,
  measureCount,
  hasPartialMeasures,
  partialMeasuresCount,
  onExportPartialMeasures,
  practiceId,
  practiceName,
  measures,
  evaluationType,
  selectedNpi,
}: ChartAbstractionExportDropdownProps) {
  const { pendingCount } = useChartAbstraction();
  const [showChartAbstractionModal, setShowChartAbstractionModal] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export
            {pendingCount > 0 && (
              <Badge className="ml-2" variant="secondary">
                {pendingCount}
              </Badge>
            )}
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-50 bg-background">
          {/* Standard Export Summary */}
          <DropdownMenuItem onClick={onExportSummary}>
            <FileText className="mr-2 h-4 w-4" />
            Export Summary
            <span className="ml-auto text-xs text-muted-foreground">
              {measureCount} measures
            </span>
          </DropdownMenuItem>

          {/* Partial Measures Export (conditional) */}
          {hasPartialMeasures && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onExportPartialMeasures}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export Partial Measures
                <Badge className="ml-auto" variant="outline">
                  {partialMeasuresCount}
                </Badge>
              </DropdownMenuItem>
            </>
          )}

          {/* Chart Abstraction List Export */}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setShowChartAbstractionModal(true)}
            disabled={pendingCount === 0}
          >
            <ClipboardList className="mr-2 h-4 w-4" />
            Export Chart Abstraction List
            {pendingCount > 0 ? (
              <Badge className="ml-auto" variant="default">
                {pendingCount}
              </Badge>
            ) : (
              <span className="ml-auto text-xs text-muted-foreground">
                0 pending
              </span>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Chart Abstraction Export Modal */}
      <ChartAbstractionExportModal
        open={showChartAbstractionModal}
        onOpenChange={setShowChartAbstractionModal}
        practiceId={practiceId}
        practiceName={practiceName}
        measures={measures}
        evaluationType={evaluationType}
        selectedNpi={selectedNpi}
      />
    </>
  );
}





